import { useAtom, useSetAtom } from 'jotai';
import LzSApp from '@lz/app/App';
import LzSAppBasics from '@lz-app/AppBasics';
import { lzFIsArray, lzFIsObject, lzFJsonToString } from './Type';
import { lzFGetTimestamp } from './Moment';
import { lzFDownloadBlobFile, lzFLogInfo, lzFShowErrorMessage } from './Utils';
import LzMUser from '@lz/models/commons/User';
import { LzTFormMethods } from '@lz/typings/types';
import lzSAuthTokenAtom from '@lz-atoms/Auth/token';
import lzSAuthUserAtom from '@lz-atoms/Auth/user';
import lzSAuthAppConfigsAtom from '@lz-atoms/Auth/appConfigs';
import lzSAuthRememberAtom from '@lz-atoms/Auth/remember';
import lzSCommonsLoadingAtom from '@lz-atoms/Commons/loading';
import lzSCommonsFormErrorsAtom from '@lz-atoms/Commons/formErrors';
import lzSCommonsLastErrorMessageAtom from '@lz-atoms/Commons/lastErrorMessage';
import LzMConfigurations from '@lz/models/commons/Configurations';

const lzFBuildAndCallApi = async (authTokenState: string, action: string, params: any, method: LzTFormMethods | undefined): Promise<Response> => {
  // Init based on action type
  let initData = {};
  let url = LzSAppBasics.api_base_url + action;

  // console.log('lzFBuildAndCallApi', authTokenState, action, params, method);

  // Setup params
  method = typeof method === 'undefined' ? 'get' : method;
  params = lzFIsObject(params) ? params : {};
  params.timestamp = lzFGetTimestamp();

  // Setup headers
  const headers: { Authorization?: string } = {};
  if (authTokenState && authTokenState !== '') {
    headers.Authorization = 'Bearer ' + authTokenState;
  }

  // Make call params in case of get request
  if ('get' === method) {
    // Edit the url with query string
    url +=
      (url.indexOf('?') < 0 ? '?' : '&') +
      Object.entries(params)
        .map((p) => p[0] + '=' + encodeURIComponent(p[1] + ''))
        .join('&');

    // Make init data
    initData = {
      method: 'GET',
      headers,
    };
  }

  // In-case of anything else, we take it as POST request
  else {
    // Set data to send for api call
    const data = new FormData();
    if (lzFIsObject(params)) {
      for (const key in params) {
        // Check for Blob type object and perform special treatment and skip loop
        if (params[key] && typeof params[key].arrayBuffer === 'function') {
          data.append(key, params[key], params[key].filename);
          continue;
        }

        data.append(
          key,
          lzFIsObject(params[key]) || lzFIsArray(params[key])
            ? lzFJsonToString(params[key])
            : params[key]
        );
      }
    }

    // Set the method (PUT, PATCH, etc) as post _method so that backend handles it accordingly
    data.append('_method', method.toUpperCase());

    // Make init data
    initData = {
      method: 'POST',
      body: data,
      headers,
    };
  }

  // Call the api with await
  lzFLogInfo('API CALL: ', url, initData);
  const response = await fetch(url, initData);
  lzFLogInfo('API RESP: ', response);

  return response;
};

const useLzFApiCall = () => {
  const [authTokenState, setAuthTokenState] = useAtom(lzSAuthTokenAtom);
  const setAuthUserState = useSetAtom(lzSAuthUserAtom);
  const setAuthAppConfigsState = useSetAtom(lzSAuthAppConfigsAtom);
  const [authRememberState, setAuthRememberState] = useAtom(lzSAuthRememberAtom);
  const setCommonsLoadingState = useSetAtom(lzSCommonsLoadingAtom);
  const setCommonsFormErrorsState = useSetAtom(lzSCommonsFormErrorsAtom);
  const setCommonsLastErrorMessageState = useSetAtom(lzSCommonsLastErrorMessageAtom);

  return async (action: string, params?: any, method?: LzTFormMethods, doNotCleanFormErrors?: boolean, doNotShowLoading?: boolean): Promise<any | null> => {
    // NOTE: We do not skip if loading because then it skips simultaneous calls. If ever simultaneous calls create a problem, then we will have to
    //       look for an ajax call handler that supports multiple calls at the same time.

    // Tell app that we are loading
    if (!doNotShowLoading) {
      setCommonsLoadingState(true);
    }

    // If not prohibited to clean form errors, then clean them (handy if you are making an intermediate api call and do not want form errors to go away)
    if (!doNotCleanFormErrors) {
      setCommonsFormErrorsState(null);
    }

    // Add the all-api-calls extra params to the api params
    const paramsWithAppApiCallsExtraParams = Object.assign({}, LzSApp.all_api_calls_extra_params, params);

    // console.log('useLzFApiCall() > Before calling lzFBuildAndCallApi() TOKEN[', authTokenState, '] AuthRemember[', authRememberState, ']');

    // Make the api call and get processed data
    const response = await lzFBuildAndCallApi(authTokenState, action, paramsWithAppApiCallsExtraParams, method);

    // console.log('Content-Type:', response.headers.get("Content-Type"));

    // If response code is 403 then it means that authentication is failed and user should be logged out
    if (403 === response.status) {
      console.error('USER LOGGED OUT! Revoking token and sending to login page...');
      setAuthTokenState('');
      setAuthUserState(null);
      setAuthAppConfigsState(null);
      if (!doNotShowLoading) {
        setCommonsLoadingState(false);
      }
      lzFShowErrorMessage(
        setCommonsLastErrorMessageState,
        'please login to continue...'
      );
      return null;
    }

    // console.log('CALL', action, response.type, response.headers.get('Content-Type'), response.headers.get('Content-Disposition'));

    // If response is a csv then show it
    const responseContentType = response?.headers?.get('Content-Type');
    const tempIndexOf1 = responseContentType?.indexOf('text/csv');
    if (typeof tempIndexOf1 !== 'undefined' && tempIndexOf1 >= 0) {
      const filename =
        response.headers
          .get('Content-Disposition')
          ?.replace('attachment; filename=', '') || 'export.csv';
      response
        .blob()
        .then((blobData) => lzFDownloadBlobFile(blobData, filename));
      if (!doNotShowLoading) {
        setCommonsLoadingState(false);
      }
      return null;
    }

    // If response is a pdf then show it
    const tempIndexOf2 = responseContentType?.indexOf('application/pdf');
    if (typeof tempIndexOf2 !== 'undefined' && tempIndexOf2 >= 0) {
      const filename =
        response.headers
          .get('Content-Disposition')
          ?.replace('attachment; filename="', '')
          ?.replace('"', '') || 'report.pdf';
      response
        .blob()
        .then((blobData) => lzFDownloadBlobFile(blobData, filename));
      if (!doNotShowLoading) {
        setCommonsLoadingState(false);
      }
      return null;
    }

    // Parse the json out of the response
    try {
      const json = await response.json();
      lzFLogInfo('API DATA: ', json);

      // Handle the data
      if (
        !json ||
        typeof json.data === 'undefined' ||
        typeof json.message === 'undefined'
      ) {
        lzFShowErrorMessage(
          setCommonsLastErrorMessageState,
          json.message || 'Invalid data returned from server.'
        );
        return null;
      }

      // Hide loading
      if (!doNotShowLoading) {
        setCommonsLoadingState(false);
      }

      // Validate success and return it if valid
      if (json.success) {
        // Check if logged in user data needs to be updated
        lzFParseLoggedInData(
          setAuthTokenState,
          setAuthUserState,
          setAuthAppConfigsState,
          setAuthRememberState,
          json.logged_in_user_data,
          authRememberState
        );

        // Return the api call data
        return json.data;
      }

      /*let errorMessage = json.message;// + '<pre>';
            const keys = Object.keys(json.data);
            for (const key of keys) {
                for (const msg of json.data[key]) {
                    errorMessage += "\n\t- " + msg;
                }
            }
            // errorMessage += '</pre>';
    
            lzFShowErrorMessage(errorMessage);*/

      lzFShowErrorMessage(setCommonsLastErrorMessageState, json.message);
      setCommonsFormErrorsState(json.data);

      return null;
    } catch (e: any) {
      console.error(e.toString());
      if (!doNotShowLoading) {
        setCommonsLoadingState(false);
      }
      lzFShowErrorMessage(
        setCommonsLastErrorMessageState,
        'An error has occured in the api call to server!<br/>Please check logs for details.'
      );
      return null;
    }
  };
};

export const lzFParseLoggedInData = (setAuthTokenState: any, setAuthUserState: any, setAuthAppConfigsState: any, setAuthRememberState: any, data: any, remember: boolean) => {
  // If data is not a valid object then skip
  if (!data) {
    return false;
  }

  // console.log('ParseLoggedInData', data);

  // If data has token and user then set them
  if (data.token && data.claims && data.rights) {
    setAuthTokenState(data.token);
    // NOTE: data.claims may only have id, name and email in it
    setAuthUserState(new LzMUser({ ...data.claims, ...data }, true));
    setAuthAppConfigsState(new LzMConfigurations(data.app_configs, true));
  }

  // Save remember flag
  setAuthRememberState(remember);

  return true;
};

export const useLzFParseLoggedInData = () => {
  const setAuthTokenState = useSetAtom(lzSAuthTokenAtom);
  const setAuthUserState = useSetAtom(lzSAuthUserAtom);
  const setAuthAppConfigsState = useSetAtom(lzSAuthAppConfigsAtom);
  const setAuthRememberState = useSetAtom(lzSAuthRememberAtom);

  return (data: any, remember: boolean): boolean =>
    lzFParseLoggedInData(
      setAuthTokenState,
      setAuthUserState,
      setAuthAppConfigsState,
      setAuthRememberState,
      data,
      remember
    );
};

export default useLzFApiCall;
